// (c) chaehongee. Confidential and proprietary.

import type { ReactElement } from 'react'
import { memo, useCallback, useRef } from 'react'
import { Bookmark } from 'lucide-react'
import { ImageItem, VideoItem, TransformOverlay, TransformOverlayBase } from '../items'
import { CropFrameOverlay, type CropFrameOverlayEdge } from './CropFrameOverlay'
import { DrawOverlay, type DrawOverlayHandle } from './DrawOverlay'
import { FrameActionButtons } from './FrameActionButtons'
import { LayerControlsOverlay } from './LayerControlsOverlay'
import type { Corner, Rect, LayerItemData } from './types'
import styles from '../../Canvas.module.css'

interface FramedImageItemProps {
    id: string // Frame ID
    layers: LayerItemData[]
    frameRect?: Rect
    contentScale: number
    getNaturalSize: (src: string) => { w: number; h: number } | null
    
    onLayerSelect: (layerId: string, e?: React.PointerEvent) => void
    onActivateLayer?: (layerId: string) => void
    onLayerMoveStart: (layerId: string, e: React.PointerEvent) => void
    onLayerResizeStart: (layerId: string, corner: Corner, e: React.PointerEvent) => void
    activeLayerId?: string
    
    onFrameSelect?: () => void
    onFrameResizeStart?: (edge: CropFrameOverlayEdge, e: React.PointerEvent) => void
    onFrameMoveStart?: (e: React.PointerEvent) => void
    
    onImageLoad: (src: string) => (e: React.SyntheticEvent<HTMLImageElement, Event>) => void
    onVideoLoad?: (src: string) => (e: React.SyntheticEvent<HTMLVideoElement, Event>) => void
    onImageError?: (src: string) => void
    
    onFrameFit?: () => void
    onFrameConfirm?: () => void
    frameSizeText?: string
    hasFrameChanges?: boolean
    hasDrawingChanges?: boolean
    frameDiffersFromImage?: boolean
    isConfirming?: boolean
    onImageContextMenu?: (layerId: string, e: React.MouseEvent, src: string) => void
    isNodeSelected?: boolean
    isDragTarget?: boolean
    // Mode controls (independent toggles)
    frameModeEnabled?: boolean
    brushModeEnabled?: boolean
    onFrameModeToggle?: () => void
    onBrushModeToggle?: () => void
    // Draw overlay controls (only active in brush mode)
    drawBrushSizeCssPx?: number
    onDrawBrushSizeChange?: (v: number) => void
    drawErasing?: boolean
    onToggleEraser?: () => void
    drawHandleRef?: React.RefObject<DrawOverlayHandle>
    readOnly?: boolean
    // Save/bookmark
    isSaved?: boolean
    onSaveToggle?: () => void
    // Layer management
    onReorderLayers?: (frameId: string, fromIndex: number, toIndex: number) => void
    onRemoveLayer?: (frameId: string, layerId: string) => void
}

export const FramedImageItem = memo(function FramedImageItem(props: FramedImageItemProps): ReactElement {
    const {
        id,
        layers,
        frameRect,
        contentScale,
        getNaturalSize,
        onLayerSelect,
        onActivateLayer,
        onLayerMoveStart,
        onLayerResizeStart,
        activeLayerId,
        onFrameSelect,
        onFrameResizeStart,
        onFrameMoveStart,
        onImageLoad,
        onVideoLoad,
        onImageError,
        onFrameFit,
        onFrameConfirm,
        frameSizeText,
        hasFrameChanges,
        hasDrawingChanges,
        frameDiffersFromImage,
        isConfirming,
        onImageContextMenu,
        isNodeSelected,
        frameModeEnabled = false,
        brushModeEnabled = false,
        onFrameModeToggle,
        onBrushModeToggle,
        drawBrushSizeCssPx,
        onDrawBrushSizeChange,
        drawErasing,
        onToggleEraser,
        drawHandleRef,
        readOnly = false,
        isSaved,
        onSaveToggle,
        onReorderLayers,
        onRemoveLayer,
    } = props

    const selected = Boolean(isNodeSelected)
    const hasFrame = Boolean(frameRect)
    const scale = 1 / (contentScale || 1)
    const isDragTargetActive = props.isDragTarget ?? false

    // Find primary layer for DrawOverlay (usually matches frame ID)
    const primaryLayer = layers.find(l => l.id === id) || layers[0]
    const primaryNaturalSize = primaryLayer ? getNaturalSize(primaryLayer.src) : null

    // Sort layers by zIndex for rendering order
    const sortedLayers = [...layers].sort((a, b) => a.zIndex - b.zIndex)
    const topMostLayer = sortedLayers[sortedLayers.length - 1] || null
    const activeLayer =
        (activeLayerId ? sortedLayers.find((l) => l.id === activeLayerId) : null) ||
        topMostLayer
    const activeLayerIsVideo = activeLayer?.mediaType === 'video'
    const allowFrameActions = !activeLayerIsVideo
    const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({})

    const handleVideoHoverStart = useCallback(() => {
        if (!activeLayer) return
        const video = videoRefs.current[activeLayer.id]
        if (!video) return
        const result = video.play()
        if (result && typeof result.catch === 'function') {
            result.catch(() => {})
        }
    }, [activeLayer])

    const handleVideoHoverEnd = useCallback(() => {
        if (!activeLayer) return
        const video = videoRefs.current[activeLayer.id]
        if (!video) return
        video.pause()
        try {
            video.currentTime = 0
        } catch {
            // Ignore seek errors (e.g., not enough data buffered)
        }
    }, [activeLayer])

    return (
        <>
            {/* Render clipped layers - Single clip container for all layers */}
            {hasFrame && frameRect ? (
                <div
                    className={styles.frameClip}
                    style={{
                        left: frameRect.x,
                        top: frameRect.y,
                        width: frameRect.w,
                        height: frameRect.h,
                        pointerEvents: 'auto',
                        zIndex: 10,
                        // Allow resize handles to sit centered on corners without being clipped.
                        overflow: 'visible',
                    }}
                    data-canvas-ui="true"
                    onPointerDown={(e) => {
                        if (e.button === 0) {
                            e.stopPropagation()
                            // Activate top-most layer by default when clicking the frame contents.
                            const layerToActivate = activeLayer?.id || topMostLayer?.id
                            if (layerToActivate) {
                                onActivateLayer?.(layerToActivate)
                                onLayerSelect(layerToActivate, e)
                            }
                        }
                    }}
                >
                    {/* Inner clip: clips images & border, but NOT handles */}
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            overflow: 'hidden',
                            borderRadius: 'var(--radius-frame)',
                        }}
                    >
                        {sortedLayers.map((layer) => (
                            layer.mediaType === 'video' ? (
                                <VideoItem
                                    key={layer.id}
                                    src={layer.src}
                                    rect={layer.rect}
                                    frameClipRect={frameRect}
                                    selected={selected}
                                    readOnly={readOnly}
                                    onLoadedMetadata={onVideoLoad?.(layer.src)}
                                    onError={() => onImageError?.(layer.src)}
                                    videoRef={(el) => {
                                        videoRefs.current[layer.id] = el
                                    }}
                                    onContextMenu={(e) => {
                                        if (readOnly) return
                                        e.preventDefault()
                                        e.stopPropagation()
                                        onImageContextMenu?.(layer.id, e, layer.src)
                                    }}
                                    onPointerDown={(e) => {
                                        if (readOnly) return
                                        if (e.button !== 0) return
                                        e.stopPropagation()
                                        onActivateLayer?.(layer.id)
                                        onLayerSelect(layer.id, e)
                                        // Start moving immediately so press-drag works on the first click.
                                        onLayerMoveStart(layer.id, e)
                                    }}
                                />
                            ) : (
                                <img
                                    key={layer.id}
                                    src={layer.src}
                                    alt={selected ? 'selected' : ''}
                                    loading="lazy"
                                    referrerPolicy="no-referrer"
                                    draggable={false}
                                    style={{
                                        position: 'absolute',
                                        left: layer.rect.x - frameRect.x,
                                        top: layer.rect.y - frameRect.y,
                                        width: layer.rect.w,
                                        height: layer.rect.h,
                                        objectFit: 'fill',
                                        pointerEvents: readOnly ? 'none' : 'auto',
                                        // Layer stack
                                        zIndex: layer.zIndex * 2,
                                    }}
                                    onLoad={onImageLoad(layer.src)}
                                    onError={() => onImageError?.(layer.src)}
                                    onContextMenu={(e) => {
                                        if (readOnly) return
                                        e.preventDefault()
                                        e.stopPropagation()
                                        onImageContextMenu?.(layer.id, e, layer.src)
                                    }}
                                    onPointerDown={(e) => {
                                        if (readOnly) return
                                        if (e.button !== 0) return
                                        e.stopPropagation()
                                        onActivateLayer?.(layer.id)
                                        onLayerSelect(layer.id, e)
                                        // Start moving immediately so press-drag works on the first click.
                                        onLayerMoveStart(layer.id, e)
                                    }}
                                />
                            )
                        ))}
                    </div>

                    {/* Saved bookmark badge + border overlay */}
                    {isSaved && (
                        <>
                            <div
                                className={styles.savedBadge}
                                style={{ transform: `scale(${scale})` }}
                                data-canvas-ui="true"
                            >
                                <Bookmark size={14} fill="currentColor" />
                            </div>
                            <div className={styles.savedBorderOverlay} data-canvas-ui="true" />
                        </>
                    )}

                    {/* Border only (NOT clipped, still occluded by upper layers).
                        In read-only mode we still show selection outline for visibility. */}
                    {selected && activeLayer && (
                        <TransformOverlayBase
                            rect={{
                                x: activeLayer.rect.x - frameRect.x,
                                y: activeLayer.rect.y - frameRect.y,
                                w: activeLayer.rect.w,
                                h: activeLayer.rect.h,
                            }}
                            selected={true}
                            contentScale={contentScale}
                            zIndex={Math.max(activeLayer.zIndex * 2 + 1, 16)}
                            enableMoveHitArea={false}
                            handlePlacement="centered"
                            showOutline={true}
                            outlineMode="cssOutline"
                            showHandles={false}
                        />
                    )}

                    {/* Handles only (NOT clipped, still occluded by upper layers via zIndex scale) */}
                    {selected && activeLayer && !readOnly && !activeLayerIsVideo && (
                        <TransformOverlayBase
                            rect={{
                                x: activeLayer.rect.x - frameRect.x,
                                y: activeLayer.rect.y - frameRect.y,
                                w: activeLayer.rect.w,
                                h: activeLayer.rect.h,
                            }}
                            selected={true}
                            contentScale={contentScale}
                            zIndex={Math.max(activeLayer.zIndex * 2 + 1, 16)}
                            enableMoveHitArea={false}
                            handlePlacement="centered"
                            showOutline={false}
                            outlineMode="cssOutline"
                            showHandles={true}
                            onResizeStart={(corner: Corner, e: React.PointerEvent) =>
                                onLayerResizeStart(activeLayer.id, corner, e)
                            }
                        />
                    )}
                </div>
            ) : (
                // No frame (fallback or initialization state)
                 sortedLayers.map((layer) => (
                    layer.mediaType === 'video' ? (
                        <VideoItem
                            key={layer.id}
                            src={layer.src}
                            rect={layer.rect}
                            selected={selected}
                            onLoadedMetadata={onVideoLoad?.(layer.src)}
                            onError={() => onImageError?.(layer.src)}
                            videoRef={(el) => {
                                videoRefs.current[layer.id] = el
                            }}
                        />
                    ) : (
                        <ImageItem
                            key={layer.id}
                            src={layer.src}
                            rect={layer.rect}
                            selected={selected}
                            onLoad={onImageLoad(layer.src)}
                            onError={() => onImageError?.(layer.src)}
                        />
                    )
                ))
            )}

            {/* Loading overlay when confirming */}
            {isConfirming && hasFrame && (
                <div
                    style={{
                        position: 'absolute',
                        left: frameRect!.x,
                        top: frameRect!.y,
                        width: frameRect!.w,
                        height: frameRect!.h,
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        pointerEvents: 'none',
                        zIndex: 10,
                    }}
                    data-canvas-ui="true"
                />
            )}

            {/* Drag target highlight overlay */}
            {isDragTargetActive && hasFrame && frameRect && (
                <div
                    style={{
                        position: 'absolute',
                        left: frameRect.x,
                        top: frameRect.y,
                        width: frameRect.w,
                        height: frameRect.h,
                        border: '3px solid rgba(59, 130, 246, 0.8)',
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        pointerEvents: 'none',
                        zIndex: 15,
                        borderRadius: '4px',
                        animation: 'pulse 1.5s ease-in-out infinite',
                    }}
                    data-canvas-ui="true"
                />
            )}

            {/* CropFrameOverlay only visible when frame mode enabled */}
            {selected && hasFrame && frameModeEnabled && (
                <CropFrameOverlay
                    rect={frameRect!}
                    selected={true}
                    contentScale={contentScale}
                    onSelect={onFrameSelect!}
                    onResizeStart={onFrameResizeStart!}
                    onDragStart={onFrameMoveStart!}
                    sizeText={frameSizeText}
                />
            )}

            {/* FrameActionButtons always visible when frame exists */}
            {selected && hasFrame && onFrameModeToggle && onBrushModeToggle && allowFrameActions && (
                <FrameActionButtons
                    rect={frameRect!}
                    scale={scale}
                    frameModeEnabled={frameModeEnabled}
                    brushModeEnabled={brushModeEnabled}
                    hasFrameChanges={hasFrameChanges ?? false}
                    hasDrawingChanges={hasDrawingChanges ?? false}
                    frameDiffersFromImage={frameDiffersFromImage ?? false}
                    isConfirming={isConfirming ?? false}
                    readOnly={readOnly}
                    isSaved={isSaved}
                    onSaveToggle={onSaveToggle}
                    onFrameModeToggle={onFrameModeToggle}
                    onBrushModeToggle={onBrushModeToggle}
                    onFitFrame={onFrameFit}
                    onConfirm={onFrameConfirm}
                />
            )}

            {/* DrawOverlay only visible when brush mode enabled - Attached to Primary Layer */}
            {selected && hasFrame && brushModeEnabled && primaryLayer && primaryLayer.mediaType !== 'video' && (
                <DrawOverlay
                    ref={drawHandleRef as any}
                    frameRect={frameRect!}
                    imageRect={primaryLayer.rect}
                    initialImageRect={primaryLayer.initialRect || null}
                    imageNaturalSize={primaryNaturalSize || null}
                    contentScale={contentScale}
                    brushSizeCssPx={Math.max(4, drawBrushSizeCssPx || 16)}
                    erasing={!!drawErasing}
                    onToggleEraser={() => onToggleEraser?.()}
                    onBrushSizeChange={(v) => onDrawBrushSizeChange?.(v)}
                />
            )}

            {/* Transform Overlay (canvas / non-framed items).
                For framed layers, border/handles are rendered within the frame stack for correct occlusion. */}
            {activeLayer && (!hasFrame || !frameRect) && (
                <TransformOverlay
                    key={`transform-${activeLayer.id}`}
                    rect={activeLayer.rect}
                    selected={selected}
                    contentScale={contentScale}
                    zIndex={20 + activeLayer.zIndex}
                    showOutline={false}
                    showHandles={!activeLayerIsVideo}
                    onPointerEnter={activeLayerIsVideo ? handleVideoHoverStart : undefined}
                    onPointerLeave={activeLayerIsVideo ? handleVideoHoverEnd : undefined}
                    onSelect={readOnly ? undefined : (e) => {
                        onActivateLayer?.(activeLayer.id)
                        onLayerSelect(activeLayer.id, e)
                    }}
                    onContextMenu={readOnly ? undefined : (e) => { e.preventDefault(); onImageContextMenu?.(activeLayer.id, e, activeLayer.src) }}
                    onMoveStart={readOnly ? undefined : (e) => onLayerMoveStart(activeLayer.id, e)}
                    onResizeStart={
                        readOnly || activeLayerIsVideo
                            ? undefined
                            : (corner, e) => onLayerResizeStart(activeLayer.id, corner, e)
                    }
                />
            )}

            {/* Layer Controls Panel - Only show when frame mode enabled and has multiple layers */}
            {selected && hasFrame && frameModeEnabled && onReorderLayers && onRemoveLayer && (
                <LayerControlsOverlay
                    frameId={id}
                    frameRect={frameRect!}
                    layers={layers}
                    contentScale={contentScale}
                    activeLayerId={activeLayer?.id}
                    onSelectLayer={(frameId, layerId) => {
                        if (frameId !== id) return
                        onActivateLayer?.(layerId)
                        onLayerSelect(layerId)
                    }}
                    onReorderLayers={onReorderLayers}
                    onRemoveLayer={onRemoveLayer}
                />
            )}
        </>
    )
})
